import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * The single loading state for Social media posts (`/home/social-media/posts`).
 *
 * Opening it used to walk through a bare route spinner (the `<Suspense>`
 * fallback), a blank `RoleRoute` frame, a narrow centred column of bars while
 * workspaces loaded, then pill-shaped placeholders where the underline network
 * tabs go, before the post grid's own card skeleton. All of those now draw this
 * one layout — network tabs, the posts filter bar and the grid — so the page
 * appears once and only the cards change when posts land.
 */

// All networks, Instagram, Facebook, LinkedIn, YouTube.
const TAB_LABEL_WIDTHS = [84, 72, 68, 64, 60];

/** Network tab placeholders — the inner row only; the caller owns the strip. */
export const SocialPostsTabItemsSkeleton: React.FC = () => (
  <div className="flex items-end gap-1" data-testid="social-posts-tabs-skeleton">
    {TAB_LABEL_WIDTHS.map((width, i) => (
      <div key={i} className="inline-flex items-center gap-2 px-3 pb-3 pt-2">
        {i > 0 ? <Skeleton className="h-4 w-4 shrink-0 rounded" /> : null}
        <Skeleton className="h-4 rounded" style={{ width }} />
        <Skeleton className="h-4 w-6 rounded-full" />
      </div>
    ))}
  </div>
);

/** Mirrors `PostsWorkspaceFilterBar`: search, four filters, filter button, view toggle, Create. */
const SocialPostsFilterBarSkeleton: React.FC = () => (
  <div className="flex flex-wrap items-center gap-2 px-[15px] py-3">
    <Skeleton className="h-9 min-w-0 flex-1 rounded-lg sm:w-[240px] sm:flex-none" />
    {[0, 1, 2, 3].map((i) => (
      <Skeleton key={i} className="hidden h-9 w-[92px] rounded-lg sm:block" />
    ))}
    <Skeleton className="h-9 w-9 rounded-lg sm:hidden" />
    <div className="flex w-full items-center justify-between gap-1.5 sm:ml-auto sm:w-auto sm:justify-start">
      <Skeleton className="h-9 w-[104px] rounded-lg" />
      <Skeleton className="h-9 w-[132px] rounded-lg" />
    </div>
  </div>
);

/** The post grid's card placeholders — shared with the page's own list load. */
export const SocialPostsGridSkeleton: React.FC = () => (
  <div className="grid gap-3 px-[15px] pb-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="social-posts-grid-skeleton">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="overflow-hidden rounded-2xl border border-[#EAECF0] bg-white">
        <Skeleton className="aspect-square w-full" />
        <div className="space-y-2 p-3">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

/** The page-level placeholder, used by the route guard, `<Suspense>` and the page's own scope wait. */
export const SocialPostsPageSkeleton: React.FC = () => (
  <div
    className="flex h-full min-w-0 flex-col overflow-hidden bg-white"
    aria-busy="true"
    aria-label="Loading social media posts"
    data-testid="social-posts-page-skeleton"
  >
    <div className="shrink-0 border-b border-[#EAECF0] bg-white px-[15px] pt-3">
      <SocialPostsTabItemsSkeleton />
    </div>
    <SocialPostsFilterBarSkeleton />
    <div className="min-h-0 flex-1 overflow-hidden">
      <SocialPostsGridSkeleton />
    </div>
  </div>
);

export default SocialPostsPageSkeleton;
